// client/src/pages/influencerpages/InfluencerMybiz.js
// 마케터 확정 셸(MarketerMybiz) 미러 — mb-* 레이아웃 + ib-theme 민트 오버라이드
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InfluencerBizHeader from '../../components/influencer/InfluencerBizHeader';
import InfluencerSidebar from '../../components/influencer/InfluencerSidebar';
import InfluencerTabbar from '../../components/influencer/InfluencerTabbar';
import InfluencerSecbar from '../../components/influencer/InfluencerSecbar';
import { warmSNSCache } from '../../utils/snsAccountCache';
import { PointProvider } from '../../contexts/PointContext';

// Import CSS — 마케터 셸 재사용 + 민트 테마
import '../../styles/marketer/mb-layout.css';
import '../../styles/marketer/mb-forms.css';
import '../../styles/marketer/mb-tables.css';
import '../../styles/marketer/mb-ui.css';
import '../../styles/marketer/mb-modal.css';
import '../../styles/influencer/ib-theme.css';
import '../../styles/influencer/InfluencerSNSCheck.css';

// 하위 화면 컴포넌트 연결
import InfluencerBankCheck from '../../components/influencer/InfluencerBankCheck';
import InfluencerPwChange from '../../components/influencer/InfluencerPwChange';
import InfluencerContractAgree from '../../components/influencer/InfluencerContractAgree';
import InfluencerMyInfoChange from '../../components/influencer/InfluencerMyInfoChange';
import InfluencerSNSCheck from '../../components/influencer/InfluencerSNSCheck';
import InfluencerSNSAdd from '../../components/influencer/InfluencerSNSAdd';

// 신규 섹션 컴포넌트
import InfluencerPointCheck from '../../components/influencer/InfluencerPointCheck';
import InfluencerPointExchange from '../../components/influencer/InfluencerPointExchange';
import InfluencerResultSend from '../../components/influencer/InfluencerResultSend';
import InfluencerProjectApply from '../../components/influencer/InfluencerProjectApply';
import InfluencerProjectApplyCheck from '../../components/influencer/InfluencerProjectApplyCheck';
import InfluencerProjectContract from '../../components/influencer/InfluencerProjectContract';
import InfluencerInquiryCheck from '../../components/influencer/InfluencerInquiryCheck';
import InfluencerInquiryFinish from '../../components/influencer/InfluencerInquiryFinish';
import InfluencerActivityHome from '../../components/influencer/InfluencerActivityHome';

const API_URL = process.env.REACT_APP_API_URL || '';

function InfluencerMybiz() {
	const nav = useNavigate();
	const location = useLocation();

	// 사이드바 상태 (모바일용)
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);

	// 활성 서브메뉴 상태
	const [activeSub, setActiveSub] = useState(null);

	// 단가 설정 완료 여부 (사이드바 뱃지 제어)
	const [snsRateComplete, setSnsRateComplete] = useState(true); // 초기: 배지 미표시

	// sns-list로 완료 여부 계산
	const checkSnsRateComplete = useCallback(async () => {
		try {
			const res = await fetch(`${API_URL}/api/influencer/mybiz/sns-list`, { credentials: 'include' });
			const data = await res.json().catch(() => ({}));
			if (!res.ok || !data.ok) return;

			const allAccounts = [
				...(data.groups?.instagram || []).map(a => ({ ...a, platform: 'instagram' })),
				...(data.groups?.blog      || []).map(a => ({ ...a, platform: 'blog' })),
				...(data.groups?.youtube   || []).map(a => ({ ...a, platform: 'youtube' })),
				...(data.groups?.tiktok    || []).map(a => ({ ...a, platform: 'tiktok' })),
			];

			if (allAccounts.length === 0) {
				// SNS 계정 0개: 배지 표시 안 함
				setSnsRateComplete(true);
				return;
			}

			// 모든 계정에 단가(플랫폼에 해당하는 필드 최소 1개) AND 카테고리(최소 1개) 설정 여부 확인
			const PLATFORM_HAS_RATE_FIELDS = {
				instagram: ['feed', 'reels'],
				blog:      ['post'],
				youtube:   ['video', 'shorts'],
				tiktok:    ['video'],
			};

			const allComplete = allAccounts.every((acc) => {
				const fields = PLATFORM_HAS_RATE_FIELDS[acc.platform] || [];
				const hasRate = fields.some(f => acc.desiredRate?.[f] != null && acc.desiredRate[f] > 0);
				const hasCat  = Array.isArray(acc.categories) && acc.categories.length > 0;
				return hasRate && hasCat;
			});

			setSnsRateComplete(allComplete);
		} catch {}
	}, []);

	// 마운트 시 완료 여부 확인
	useEffect(() => {
		checkSnsRateComplete();
	}, [checkSnsRateComplete]);

	// URL 쿼리 파라미터와 상태 동기화 (URL 변경 시마다 실행)
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const sub = params.get('sub');
		setActiveSub(sub || null);
		// 화면 전환 시 사이드바 닫기 (모바일)
		setIsSidebarOpen(false);
	}, [location.search]);

	// 접근 가드(인플루언서만)
	useEffect(() => {
		let ignore = false;
		(async () => {
			try {
				const res = await fetch('/api/user/me', { credentials: 'include' });
				const data = await res.json().catch(() => ({}));
				if (!res.ok || !data.ok || data.role !== 'influencer') {
					if (!ignore) nav('/');
					return;
				}

				warmSNSCache();
			} catch {
				if (!ignore) nav('/');
				return;
			}
		})();
		return () => { ignore = true; };
	}, [nav]);

	// 조건부 렌더링 (projectApply는 폼 상태 유지를 위해 별도 display 토글)
	const renderComponent = () => {
		switch (activeSub) {
			// 1. 프로젝트
			case 'projectApplyCheck': return <InfluencerProjectApplyCheck />;
			case 'projectContract': return <InfluencerProjectContract />;
			case 'resultSend': return <InfluencerResultSend />;

			// 2. 포인트
			case 'pointList': return <InfluencerPointCheck />;
			case 'pointExchange': return <InfluencerPointExchange />;

			// 3. 내 정보
			case 'snsView': return <InfluencerSNSCheck onSaved={checkSnsRateComplete} />;
			case 'snsAdd': return <InfluencerSNSAdd />;
			case 'bank': return <InfluencerBankCheck />;
			case 'contract': return <InfluencerContractAgree />;
			case 'profile': return <InfluencerMyInfoChange />;
			case 'password': return <InfluencerPwChange />;

			// 4. 고객센터
			case 'inquiryCheck': return <InfluencerInquiryCheck />;
			case 'inquiryFinish': return <InfluencerInquiryFinish />;

			// 기본 진입 화면 = 활동 홈
			default:
				return <InfluencerActivityHome />;
		}
	};

	return (
		<PointProvider userRole="influencer">
			<div className="mb-layout-container ib-theme">
				{/* Header — 햄버거로 모바일 드로어 제어 */}
				<InfluencerBizHeader onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

				{/* Sidebar */}
				<InfluencerSidebar
					isOpen={isSidebarOpen}
					onClose={() => setIsSidebarOpen(false)}
					showSnsBadge={!snsRateComplete}
				/>

				{/* Main Content */}
				<main className="mb-main-content">
					{/* 컨텍스트 칩바 (모바일 2차 내비) */}
					<InfluencerSecbar />

					{/* 프로젝트 지원하기는 폼 데이터 유지를 위해 항상 렌더링 (display toggle) */}
					<div style={{ display: activeSub === 'projectApply' ? 'block' : 'none' }}>
						<InfluencerProjectApply />
					</div>
					{activeSub !== 'projectApply' && renderComponent()}
				</main>

				{/* Mobile Bottom Tabbar */}
				<InfluencerTabbar />

				{/* Mobile Sidebar Overlay */}
				{isSidebarOpen && (
					<div
						style={{
							position: 'fixed',
							top: 0, left: 0, right: 0, bottom: 0,
							backgroundColor: 'rgba(0,0,0,0.5)',
							zIndex: 1044 /* 드로어(1045) 바로 아래, 탭바(1040) 위 */
						}}
						onClick={() => setIsSidebarOpen(false)}
					/>
				)}
			</div>
		</PointProvider>
	);
}

export default InfluencerMybiz;
